import { FC, MouseEvent } from 'react';

import classNames from 'classnames';

import clickmeVideoPortfolioCancelSelectionButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/clickme_video_portfolio_cancel_selection_button_click';
import clickmeVideoPortfolioDeleteIconButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/clickme_video_portfolio_delete_icon_button_click';
import clickmeVideoPortfolioPlayButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/clickme_video_portfolio_play_button_click';
import clickmeVideoPortfolioSelectButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/clickme_video_portfolio_select_button_click';
import { BottomSheet, Button, useBreakpoint } from '@hh.ru/magritte-ui';
import {
    CheckCircleFilledSize16,
    CircleOutlinedSize16,
    PlayFilledSize24,
    TrashOutlinedSize16,
    TrashOutlinedSize24,
} from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { RemoveAlert } from 'components/common/BannerVideo/VideoGallery/RemoveAlert/RemoveAlert';

import { resetBannerVideo, setBannerVideo } from 'store/models/createBanner';
import { resetSelectedVideo, setSelectedVideoUploadId, VideoGalleryItem } from 'store/models/videoGallery';
import { useDispatch } from 'store/utils';

import styles from 'components/common/BannerVideo/VideoGallery/VideoPreviewItem/VideoPreviewItem.module.less';

interface VideoPreviewItemProps {
    video: VideoGalleryItem;
    isSelected: boolean;
    onClick: () => void;
}

const TrlKeys = {
    choose: 'AdCreate.AdForm.BannerVideo.VideoGallery.choose',
    cancel: 'AdCreate.AdForm.BannerVideo.VideoGallery.cancel',
};

export const VideoPreviewItem: FC<VideoPreviewItemProps> = ({ video, isSelected, onClick }) => {
    const [isRemoveAlertVisible, setIsRemoveAlertVisibleOn, setIsRemoveAlertVisibleOff] = useOnOffState(false);
    const [isBottomSheetVisible, setIsBottomSheetVisibleOn, setIsBottomSheetVisibleOff] = useOnOffState(false);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isMobile } = useBreakpoint();

    const { videoUploadId } = video;

    const handlePreviewClick = () => {
        clickmeVideoPortfolioPlayButtonClick();
        onClick();
    };

    const handleSelect = (e: MouseEvent) => {
        e.stopPropagation();
        clickmeVideoPortfolioSelectButtonClick();
        isMobile && setIsBottomSheetVisibleOff();
        dispatch([setBannerVideo(video), setSelectedVideoUploadId(videoUploadId)]);
    };

    const handleReset = (e: MouseEvent) => {
        e.stopPropagation();
        clickmeVideoPortfolioCancelSelectionButtonClick();
        isMobile && setIsBottomSheetVisibleOff();
        dispatch([resetSelectedVideo(null), resetBannerVideo(null)]);
    };

    const handleRemove = (e: MouseEvent) => {
        e.stopPropagation();
        clickmeVideoPortfolioDeleteIconButtonClick();
        setIsRemoveAlertVisibleOn();
    };

    return (
        <>
            <div
                className={classNames(styles.previewWrapper, {
                    [styles.selected]: isSelected,
                })}
                onClick={isMobile ? setIsBottomSheetVisibleOn : handlePreviewClick}
                data-qa="video-preview-item-container"
            >
                <img className={styles.preview} src={video.thumbnailUrl} />
                {!isMobile && (
                    <div className={styles.overlay}>
                        <div className={classNames(styles.overlayIcon, styles.overlaySelectIcon)}>
                            {isSelected ? (
                                <CheckCircleFilledSize16
                                    onClick={handleReset}
                                    initialColor="contrast"
                                    data-qa="video-preview-item-reset"
                                />
                            ) : (
                                <CircleOutlinedSize16
                                    onClick={handleSelect}
                                    initialColor="contrast"
                                    data-qa="video-preview-item-select"
                                />
                            )}
                        </div>
                        <div className={styles.overlayIcon}>
                            <PlayFilledSize24 initialColor="contrast" />
                        </div>
                        <div className={classNames(styles.overlayIcon, styles.overlayRemoveIcon)}>
                            <TrashOutlinedSize16
                                onClick={handleRemove}
                                initialColor="contrast"
                                data-qa="video-preview-item-remove"
                            />
                        </div>
                    </div>
                )}
            </div>
            <BottomSheet
                height="content"
                visible={isBottomSheetVisible}
                onClose={setIsBottomSheetVisibleOff}
                showDivider="with-scroll"
                footer={
                    <div className={styles.buttons}>
                        <Button
                            mode="secondary"
                            style="neutral"
                            icon={<TrashOutlinedSize24 />}
                            hideLabel
                            onClick={handleRemove}
                            data-qa="video-preview-item-remove"
                        />
                        <Button
                            mode="primary"
                            style="neutral"
                            stretched
                            onClick={isSelected ? handleReset : handleSelect}
                            data-qa={isSelected ? 'video-preview-item-reset' : 'video-preview-item-select'}
                        >
                            {trls(isSelected ? TrlKeys.cancel : TrlKeys.choose)}
                        </Button>
                    </div>
                }
            >
                <div
                    className={styles.mobileWrapper}
                    onClick={() => {
                        setIsBottomSheetVisibleOff();
                        handlePreviewClick();
                    }}
                    data-qa="video-preview-item-mobile-preview"
                >
                    <img className={styles.preview} src={video.thumbnailUrl} />
                    <div className={styles.mobileOverlay}>
                        <PlayFilledSize24 initialColor="constant" />
                    </div>
                </div>
            </BottomSheet>
            <RemoveAlert
                visible={isRemoveAlertVisible}
                videoUploadId={videoUploadId}
                onClose={setIsRemoveAlertVisibleOff}
            />
        </>
    );
};
